"use client"

import { observer } from "mobx-react-lite"
import {
  type IRateAssignment,
  defaultRatingAssignment,
} from "../../../shared/models/three-sixty-feedback-models/RateAssignments"
import { type FormEvent, useState } from "react"
import { useAppContext } from "../../../shared/functions/Context"
import { hideModalFromId } from "../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../dialogs/ModalName"
import { Calendar } from "lucide-react"

const AddTermRating = observer(() => {
  const { api, store } = useAppContext()

  const [rateAssignment, setRateAssignment] = useState<IRateAssignment>({
    ...defaultRatingAssignment,
    description: defaultRatingAssignment.description || "",
  })
  const [fromYear, setFromYear] = useState("")
  const [toYear, setToYear] = useState("")
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(true)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const description = `${fromYear}-${toYear}`
    const updatedAssignment = {
      ...rateAssignment,
      description,
    }

    setLoading(true)

    const selected = store.ratingAssignments.selected

    if (selected) await update(updatedAssignment)
    else await create(updatedAssignment)

    setLoading(false)
    onCancel()
  }

  const update = async (rateAssignment: IRateAssignment) => {
    try {
      await api.ratingAssignments.update(rateAssignment)
    } catch (error) {
      console.log("Failed to update:", error)
    }
  }

  const create = async (rateAssignment: IRateAssignment) => {
    try {
      await api.ratingAssignments.create(rateAssignment)
    } catch (error) {
      console.log("Failed to create:", error)
    }
  }

  const onCancel = () => {
    store.ratingAssignments.clearSelected()
    setRateAssignment({ ...defaultRatingAssignment })
    setFromYear("")
    setToYear("")
    setIsOpen(false)
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_RATING_TERM)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-blue-100 text-blue-600">
            <Calendar className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Feedback Term</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="from-year" className="text-sm font-medium text-gray-700">
                From Year
              </label>
              <input
                id="from-year"
                type="number"
                placeholder="2024"
                min="1900"
                max="2100"
                value={fromYear}
                onChange={(e) => setFromYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="to-year" className="text-sm font-medium text-gray-700">
                To Year
              </label>
              <input
                id="to-year"
                type="number"
                placeholder="2025"
                min="1900"
                max="2100"
                value={toYear}
                onChange={(e) => setToYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !fromYear.trim() || !toYear.trim()}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default AddTermRating
